/**
 * Zero-results rescue math — toolkit `flexible-search` half (b) (#1265, parity
 * row `toolkit.flexible-search`).
 *
 * Transcribed from the sitewide FlexibleSearch MODULE — not the Searchbar Pro
 * bridge that flexDates.ts ports. Sources, all in homerunner-toolkit:
 *   - `exactAlternativeChips`  assets/js/flexible-search.js:544-574
 *   - verification cap         RestController::MAX_RANGES (= 8), js:49-55, :565-572
 *   - abuse bounds             RestController::MAX_HORIZON_DAYS / MAX_NIGHTS, :47-55, :172-205
 *   - chip label `fmtChipLabel` js:95-100
 *   - verdict fold `applyResults` assets/js/flex-availability.js:96-128
 *   - knob bounds/defaults     includes/FlexibleSearch/Settings.php:46-58, :78-79
 *
 * Pure `YYYY-MM-DD` string math like flexDates.ts, so the whole surface is
 * testable without a DOM. Callers convert at the boundary.
 */
import { format } from 'date-fns'
import { addDays } from '../flexDates'

/** `Settings::DEFAULTS` window_before_weeks / window_after_weeks (Settings.php:53-54). */
export const DEFAULT_WINDOW_BEFORE_WEEKS = 2
export const DEFAULT_WINDOW_AFTER_WEEKS = 4
/** `Settings::normalize_weeks` bounds (Settings.php:78-79). */
export const WINDOW_BEFORE_WEEKS_RANGE: readonly [number, number] = [0, 8]
export const WINDOW_AFTER_WEEKS_RANGE: readonly [number, number] = [1, 12]
/**
 * The most windows one verification pass checks — the module's REST endpoint
 * caps `ranges` at 8 and the frontend samples down to it (js:565-572). Here
 * every window is one `feed/properties` probe, so the cap bounds the fan-out.
 */
export const MAX_VERIFIABLE_WINDOWS = 8
/**
 * Abuse bounds, same values as the module's endpoint (RestController.php:54-55):
 * no chip this feature produces looks further out than the picker horizon or
 * covers a stay this long, so a hand-edited URL past them gets no windows —
 * rather than minting probes for it. The horizon also keeps the arithmetic
 * inside representable dates: `?checkin=9999-12-25` used to shift into year
 * 10000, where `toISOString()` yields `+010000-…` and every window collapsed
 * onto one malformed pair (duplicate keys, garbage ranges).
 */
export const MAX_HORIZON_DAYS = 800
export const MAX_NIGHTS = 180

export type RescueWindow = { checkin: string; checkout: string; label: string }
/**
 * `true`  — Central confirmed at least one bookable home in the window.
 * `false` — Central answered and nothing is bookable (the chip prunes).
 * `null`  — Central could not answer (transport/server error); the chip stays,
 *           unverified — a broken check must never make suggestions vanish.
 */
export type Verdict = boolean | null
export type RescueChip = RescueWindow & { verified: boolean }

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Strict `YYYY-MM-DD` naming a real calendar date (flex-availability.js:32-37).
 * Impossible dates (2026-02-31) are rejected, not rolled over.
 */
export function isIsoDate(v: unknown): v is string {
  if (typeof v !== 'string' || !ISO_RE.test(v)) return false
  const t = Date.parse(v + 'T00:00:00Z')
  if (Number.isNaN(t)) return false
  return new Date(t).toISOString().slice(0, 10) === v
}

/** Both ends real dates and checkout strictly after checkin. */
export function validRange(checkin: unknown, checkout: unknown): checkin is string {
  return isIsoDate(checkin) && isIsoDate(checkout) && checkout > checkin
}

/** Whole nights between two dates (UTC midnights, so DST can never shave one). */
export function nightsBetween(checkin: string, checkout: string): number {
  const a = Date.parse(checkin + 'T00:00:00Z')
  const b = Date.parse(checkout + 'T00:00:00Z')
  return Math.max(1, Math.round((b - a) / 86_400_000))
}

/**
 * Window label, e.g. "Aug 8 - 15" or "Aug 29 - Sep 5". Module `fmtChipLabel`:
 * printed to the CHECKOUT day (unlike the SBP strip's last-night labels in
 * flexDates.ts), hyphen-separated, second month only when it changes. The
 * notice sentence interpolates the same label, so the two surfaces agree.
 */
export function rescueRangeLabel(checkin: string, checkout: string): string {
  const ci = new Date(checkin + 'T00:00:00')
  const co = new Date(checkout + 'T00:00:00')
  const head = `${format(ci, 'MMM')} ${ci.getDate()}`
  return checkin.slice(0, 7) === checkout.slice(0, 7)
    ? `${head} - ${co.getDate()}`
    : `${head} - ${format(co, 'MMM')} ${co.getDate()}`
}

/**
 * A week knob as the module reads it (js:46-47 / Settings::normalize_weeks):
 * integers clamp into `[min, max]`; anything non-numeric is the default.
 */
export function clampWeeks(raw: unknown, fallback: number, [min, max]: readonly [number, number]): number {
  const n = typeof raw === 'number' ? raw
    : typeof raw === 'string' && /^-?\d+$/.test(raw) ? parseInt(raw, 10)
    : NaN
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, Math.trunc(n)))
}

/** Identity of a window inside one search: "checkin|checkout". */
export function windowKey(w: { checkin: string; checkout: string }): string {
  return `${w.checkin}|${w.checkout}`
}

/**
 * Same-length windows shifted around the searched dates: the nearest week
 * before first, then forward week by week (`exactAlternativeChips`). Check-ins
 * before `today` are dropped, today itself stays suggestible (same-day check-in
 * is bookable); check-ins past `today + MAX_HORIZON_DAYS` are dropped too, and
 * a stay over MAX_NIGHTS gets no windows at all (`parse_ranges`). Every window
 * is re-validated and de-duplicated before it is returned, so nothing
 * malformed can reach a key or a request. An invalid or inverted range yields
 * nothing.
 *
 * More than `cap` windows sample evenly across the full span — boundaries
 * included — so widening the window still reaches its far edge; chronological
 * order is preserved by construction.
 */
export function rescueWindows(
  checkin: string,
  checkout: string,
  today: string,
  beforeWeeks: number,
  afterWeeks: number,
  cap: number = MAX_VERIFIABLE_WINDOWS,
): RescueWindow[] {
  if (!validRange(checkin, checkout) || !isIsoDate(today)) return []
  const nights = nightsBetween(checkin, checkout)
  if (nights > MAX_NIGHTS) return []
  const latest = addDays(today, MAX_HORIZON_DAYS)

  const offsets: number[] = []
  for (let b = beforeWeeks; b >= 1; b--) offsets.push(-7 * b)
  for (let a = 1; a <= afterWeeks; a++) offsets.push(7 * a)

  let out: RescueWindow[] = []
  const seen = new Set<string>()
  for (const days of offsets) {
    const ci = addDays(checkin, days)
    if (ci < today || ci > latest) continue
    const co = addDays(ci, nights)
    if (!validRange(ci, co)) continue
    const key = windowKey({ checkin: ci, checkout: co })
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ checkin: ci, checkout: co, label: rescueRangeLabel(ci, co) })
  }
  if (out.length > cap && cap > 1) {
    const last = out.length - 1
    out = Array.from({ length: cap }, (_, k) => out[Math.round((k * last) / (cap - 1))])
  }
  return out
}

/**
 * Fold verdicts onto the window list (`applyResults`): `false` prunes, `true`
 * verifies, `null`/`undefined` (unknown / not yet answered) keeps the window
 * unverified. Returns the surviving chips in the original order.
 */
export function applyVerdicts(
  windows: RescueWindow[],
  verdictFor: (w: RescueWindow) => Verdict | undefined,
): { chips: RescueChip[]; removed: number; verified: number } {
  const chips: RescueChip[] = []
  let removed = 0
  let verified = 0
  for (const w of windows) {
    const v = verdictFor(w)
    if (v === false) {
      removed++
      continue
    }
    if (v === true) verified++
    chips.push({ ...w, verified: v === true })
  }
  return { chips, removed, verified }
}
